import { DEFAULT_TCO } from '../DesignHuddle/constants'
import { DHTco } from '../../../types/design-huddle.types'
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react'
import { FlowcodeType } from '@dtx-company/inter-app/src/types/flowcode'
import { RootCodeState } from '@dtx-company/inter-app/src/redux/types'
import { getFlowcodeGeneratorRootUrl } from '@dtx-company/true-common/src/utils/urls/services'
import { getFlowcodeUrl } from '@dtx-company/flowcode-generator-browser/src/getFlowcodeUrl'
import { getFormattedFlowcodeOptions } from '../../../utils/flowcode'
import { getIdFromEncodedString } from '@dtx-company/true-common/src/utils/strings'
import { getRedirectType } from '@app/code/src/utils/flowcode/getRedirectType'
import {
  hydrateFromUrl,
  setSelectedFlowcode
} from '@dtx-company/inter-app/src/redux/slices/printStoreApiSlice/printStoreSlice'
import { isStaticRedirectType } from '@app/code/src/utils/flowcode/isStaticRedirectType'
import { useBrandKitTco } from '../DesignHuddle/hooks'
import { useCodeSelector } from '@dtx-company/inter-app/src/hooks/useCodeSelector'
import { useDispatch } from 'react-redux'
import { useFlowRouter } from '@dtx-company/inter-app/src/hooks/useFlowRouter'
import cloneDeep from 'lodash/cloneDeep'
import events from '@dtx-company/inter-app/src/event-tracking/events/templatesStore'

export interface EditTemplatePageSecondaryNavState {
  clearCode: () => void
  backButtonHandler: () => void
  tco: DHTco
  setTco: Dispatch<SetStateAction<DHTco>>
}

export const getFlowcodeGeneratorURL = (selectedFlowcode: FlowcodeType): string => {
  if (selectedFlowcode) {
    const studioConfigId = selectedFlowcode.studioConfig?.id
    if (studioConfigId && studioConfigId.length) {
      const decodedStudioConfigId = getIdFromEncodedString(studioConfigId)
      const redirectType = getRedirectType(selectedFlowcode.activeLink.redirectValue || '')
      const data = isStaticRedirectType(redirectType)
        ? selectedFlowcode.activeLink.redirectValue
        : selectedFlowcode.activeLink.shorturl
      return `${getFlowcodeGeneratorRootUrl()}/v1/flowcode?studio_config_id=${decodedStudioConfigId}&imageType=png&data=${encodeURIComponent(
        data
      )}`
    } else {
      const options = getFormattedFlowcodeOptions(selectedFlowcode)
      return getFlowcodeUrl(options)
    }
  }
  return ''
}

export const useEditTemplatePageSecondaryNavState = (): EditTemplatePageSecondaryNavState => {
  const { selectedFlowcode } = useCodeSelector((state: RootCodeState) => state.printStore)
  const brandKitTco = useBrandKitTco()
  const [tco, setTco] = useState<DHTco>(brandKitTco)
  const router = useFlowRouter()
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(
      hydrateFromUrl({
        batchId: router.getString('batchId'),
        dhTemplateId: router.getString('dhTemplateId'),
        templateCategoryId: router.getString('templateCategoryId'),
        productId: router.getString('productId'),
        templateId: router.getString('templateId')
      })
    )
  }, [dispatch, router])

  const flowcodeImage = useMemo(() => {
    if (selectedFlowcode) {
      return getFlowcodeGeneratorURL(selectedFlowcode)
    } else {
      return DEFAULT_TCO.flowcodeImage.url || ''
    }
  }, [selectedFlowcode])

  useEffect(() => {
    if (tco.flowcodeImage.url !== flowcodeImage) {
      const updatedTco = cloneDeep(tco)
      updatedTco.flowcodeImage.url = flowcodeImage
      setTco(updatedTco)
    }
  }, [flowcodeImage, tco])

  const clearCode = (): void => {
    dispatch(setSelectedFlowcode(undefined))
    events.Templates_Editor_Clicked_RemoveFlowcodeButton()
  }

  const backButtonHandler = (): void => {
    router.back()
  }

  return {
    clearCode,
    backButtonHandler,
    tco,
    setTco
  }
}
